import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { usePermissions } from '@/lib/permissionsStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  /** RBAC stage b: staff whose roles grant this permission also pass. */
  perm?: string;
}

export function ProtectedRoute({ children, adminOnly = false, perm }: ProtectedRouteProps) {
  const { user, profile, loading, profileLoaded, profileError } = useAuthStore();
  const { perms, can } = usePermissions();
  const location = useLocation();

  // Still initializing auth
  if (loading) {
    return <Spinner />;
  }

  // Not logged in
  if (!user) {
    // Admin routes: send to /login (admin will sign in there).
    // Customer routes (only `/` today): there's no customer login —
    // accounts are not part of the COD flow — so /login is the WRONG
    // destination. Send anonymous customers back to the storefront,
    // optionally jumping straight to checkout if a flow had stashed
    // a `nextUrl` for the order they were just on.
    if (adminOnly) {
      return (
        <Navigate
          to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
          replace
        />
      );
    }
    return <AnonymousCustomerRedirect />;
  }

  // ⚠️ SIGNED IN, ROLE NOT KNOWN YET: WAIT — or say we cannot reach the server.
  // Never judge the door on a missing profile: « not admin » used to bounce an
  // admin to « / », the customer « Mes commandes » (owner, 2026-09-25: « it's
  // like there are two gateways »). See store/profileLoad.ts.
  if (!profile) {
    return profileError ? <ProfileErrorScreen /> : <Spinner />;
  }
  if (adminOnly && !profileLoaded) {
    return <Spinner />;
  }

  // Admin check — profile is loaded by now. RBAC stage b: a `perm` prop lets
  // staff through when one of their roles grants it; admin always passes.
  if (adminOnly && profile?.role !== 'admin') {
    if (perm && profile?.role === 'staff') {
      if (!perms) return <Spinner />;   // permission set still loading
      if (can(perm)) return <>{children}</>;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Used in place of /login for anonymous customers who hit a guarded
 * customer route (typically `/`). Forwards to the COD-form-stashed
 * checkout URL if present, else the storefront. Effect (not Navigate)
 * because the destination is cross-origin.
 */
function AnonymousCustomerRedirect() {
  useEffect(() => {
    let target = 'https://storiz.ma';
    try {
      const stashed = sessionStorage.getItem('storiz_post_submit_next');
      if (stashed) {
        sessionStorage.removeItem('storiz_post_submit_next');
        target = stashed;
      }
    } catch { /* ignore */ }
    window.location.replace(target);
  }, []);
  return <Spinner />;
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * The server could not say who this is. Say exactly that, with the two ways
 * out — never a page that belongs to someone else.
 */
export function ProfileErrorScreen() {
  const { refreshProfile, signOut } = useAuthStore();
  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-bg-primary">
      <div role="alert" className="w-full max-w-sm rounded-2xl border border-border-subtle bg-bg-card px-6 py-7 text-center">
        <h1 className="font-heading text-[22px] leading-tight text-text-primary">Connexion au serveur impossible</h1>
        <p className="text-[14px] text-text-secondary mt-2 leading-relaxed">
          Nous n’arrivons pas à vérifier votre compte. Vérifiez votre connexion internet, puis réessayez.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => { void refreshProfile(); }}
            className="h-11 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[14px] font-semibold cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500"
          >
            Réessayer
          </button>
          <button
            type="button"
            onClick={() => { void signOut(); }}
            className="h-10 rounded-lg text-[13px] text-text-secondary hover:text-text-primary cursor-pointer"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}
